import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { yupResolver } from "@hookform/resolvers/yup";
import PropTypes from "prop-types";
import React from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

const projectSchema = yup
  .object({
    name: yup
      .string()
      .trim()
      .required("Project name is required")
      .min(3, "Name must be at least 3 characters")
      .max(50, "Name must be less than 50 characters"),
    description: yup
      .string()
      .max(500, "Description must be less than 500 characters")
      .nullable(),
  })
  .required();

const EditProjectForm = ({ project, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
  } = useForm({
    resolver: yupResolver(projectSchema),
    mode: "all",
    defaultValues: {
      name: project?.name || "",
      description: project?.description || "",
    },
  });

  const nameValue = watch("name");
  const isNameValid = nameValue?.trim().length >= 3;
  const isSubmitDisabled = !isNameValid || !isDirty;

  const handleOnSubmit = (data) => {
    onSubmit({
      ...project,
      ...data,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-right">
          Name<span className="text-orange-500">*</span>
        </Label>
        <Input
          {...register("name")}
          id="name"
          placeholder="Project name"
          className={errors.name ? "border-red-500 focus:border-red-500" : ""}
          aria-invalid={errors.name ? "true" : "false"}
          autoComplete="off"
        />
        {errors.name && (
          <p className="text-sm text-red-500" role="alert">
            {errors.name.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description" className="text-right">
          Description
        </Label>
        <Textarea
          {...register("description")}
          id="description"
          placeholder="Project description"
          className={
            errors.description ? "border-red-500 focus:border-red-500" : ""
          }
          aria-invalid={errors.description ? "true" : "false"}
          rows={4}
        />
        {errors.description && (
          <p className="text-sm text-red-500" role="alert">
            {errors.description.message}
          </p>
        )}
      </div>

      <div className="flex justify-between items-center pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          className="font-semibold"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitDisabled}
          className="font-semibold"
        >
          Update Project
        </Button>
      </div>
    </form>
  );
};

EditProjectForm.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default EditProjectForm;
